import { useState, useEffect } from 'react';
import { Table, Card, ScrollArea, LoadingOverlay, Box } from '@mantine/core';
import { SortableTableHeader, NonSortableColumnHeader } from '../../common/SortableTableHeader';
import { ColumnAveragesRow } from '../../common/ConfigurableTable';
import { CollapsibleGroupHeader } from '../components/CollapsibleGroupHeader';
import { Pagination } from '../../common/Pagination';
import type { SortConfig, ColumnConfig } from '../../../hooks/useColumnConfig';
import type { DataLibrarySessionRow, ColumnAverages } from '../../../types';
import { renderSessionCell } from './SessionsCellRenderers';
import { FLAG_SEVERITY } from '../../../config/severityConfig';
import classes from '../../common/ConfigurableTable/ConfigurableTable.module.css';
import { flattenGroups, type GroupNode } from '../../../utils/groupDataByField';
import { ResizableHeaderCell } from '../components/ResizableHeaderCell';
import { useDataLibraryColumnWidths } from '../useDataLibraryTableLayout';
import { ColumnWidthCell } from '../components/ColumnWidthCell';
import { DataLibraryEmptyState } from '../components/DataLibraryEmptyState';

interface SessionsTableProps {
  data: DataLibrarySessionRow[];
  groupedData: GroupNode<DataLibrarySessionRow>[] | null;
  columns: ColumnConfig[];
  sortConfig: SortConfig | null;
  onSort: (key: string) => void;
  loading: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  page: number;
  pageSize: number;
  totalCount: number;
  /** MDF-41: server averages over all matching rows */
  averages: ColumnAverages;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  hidePagination?: boolean;
}

// Columns whose cell has its own Tooltip get no title. Add a new one here, or a cut cell shows two boxes.
const TOOLTIP_COLUMNS = ['health_nudges', 'session_attendance_rate', 'attendance', 'engagement', 'airtime', 'participants'];

function getColStyle(colId?: string): React.CSSProperties {
  const wide = colId === 'attendance' || colId === 'engagement' || colId === 'participants' || colId === 'internal_notes';
  const frozen = colId === 'group_session';
  return { minWidth: wide ? 400 : frozen ? 260 : 200, whiteSpace: 'nowrap', overflow: 'hidden' };
}

function SessionRow({
  widths,
  row,
  columns,
}: {
  widths: Record<string, number>;
  row: DataLibrarySessionRow;
  columns: ColumnConfig[];
}) {
  return (
    <Table.Tr
      className={classes.tableRow}
      style={row.is_flagged ? { backgroundColor: FLAG_SEVERITY.medium.row.background } : undefined}
    >
      {columns.map((col, index) => (
        <ColumnWidthCell
          key={col.id}
          className={index === 0 ? classes.stickyColumn : undefined}
          style={getColStyle(col.id)}
          width={widths[col.id]}
          hasTooltip={TOOLTIP_COLUMNS.includes(col.id)}
        >
          {renderSessionCell(row, col.id)}
        </ColumnWidthCell>
      ))}
    </Table.Tr>
  );
}


export function SessionsTable({
  data,
  groupedData,
  columns,
  sortConfig,
  onSort,
  loading,
  hasActiveFilters,
  onClearFilters,
  page,
  pageSize,
  totalCount,
  averages,
  totalPages,
  onPageChange,
  onPageSizeChange,
  hidePagination = false,
}: SessionsTableProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const layout = useDataLibraryColumnWidths();

  const toggleGroup = (key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const groupKeys = groupedData ? groupedData.map(g => g.key).join('\0') : '';
  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [groupKeys]);

  const averagesRow = (
    <ColumnAveragesRow
      columns={columns}
      averages={averages}
      rows={data}
      totalCount={totalCount}
      firstCellClassName={classes.stickyColumn}
    />
  );

  const hasData = data.length > 0 || (groupedData && groupedData.length > 0);
  const showEmpty = groupedData ? groupedData.length === 0 : !loading && data.length === 0;

  const tableHeader = (
    <Table.Thead className={classes.tableHeader}>
      <Table.Tr>
        {columns.map((col, index) =>
          col.sortable ? (
            <ResizableHeaderCell key={col.id} column={col} sticky={index === 0} style={getColStyle(col.id)} layout={layout}>
              <SortableTableHeader label={col.label} sortKey={col.id} currentSort={sortConfig} onSort={onSort} />
            </ResizableHeaderCell>
          ) : (
            <ResizableHeaderCell key={col.id} column={col} sticky={index === 0} style={getColStyle(col.id)} layout={layout}>
              <NonSortableColumnHeader label={col.label} />
            </ResizableHeaderCell>
          ),
        )}
      </Table.Tr>
    </Table.Thead>
  );

  const showPagination = !hidePagination && totalCount > 0 && (!loading || !!hasData);

  const tableContent = (
    <>
      {tableHeader}
      <Table.Tbody>
        {averagesRow}
        {groupedData
          ? flattenGroups(groupedData, collapsedGroups).map(item =>
                'group' in item
                  ? <CollapsibleGroupHeader key={`group-${item.group.key}`} label={item.group.label} count={item.group.rows.length} depth={item.group.depth} colSpan={columns.length} columns={columns} rows={item.group.rows} isCollapsed={collapsedGroups.has(item.group.key)} onToggle={() => toggleGroup(item.group.key)} entityLabel={item.group.rows.length === 1 ? 'session' : 'sessions'} />
                  : <SessionRow key={item.row.session_id} row={item.row} columns={columns} widths={layout.columnWidths} />,
              )
          : <>
              {data.map(row => <SessionRow key={row.session_id} row={row} columns={columns} widths={layout.columnWidths} />)}
            </>
        }
      </Table.Tbody>
    </>
  );

  return (
    <Card withBorder p={0} radius={8} bg="white" style={{ boxShadow: 'none', position: 'relative' }} className={classes.tableCard}>
      <LoadingOverlay visible={loading && !!hasData} overlayProps={{ blur: 1 }} />
      <ScrollArea scrollbarSize={8} type="auto" offsetScrollbars={false}>
        <Table highlightOnHover className={classes.table}>
          {tableContent}
        </Table>
      </ScrollArea>
      {showEmpty && <DataLibraryEmptyState message="No session data yet" hasActiveFilters={hasActiveFilters} onClearFilters={onClearFilters} />}
      {showPagination && (
        <Box className={classes.paginationWrapper}>
          <Pagination page={page} pageSize={pageSize} total={totalCount} totalPages={totalPages} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} pageSizeOptions={[25, 50, 100, 250]} entityLabel="sessions" />
        </Box>
      )}
    </Card>
  );
}
